import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

/**
 * Barra superior de las pantallas que muestran contenido incrustado: el visor de
 * dashboards, el visor de BelivPedia y el sitio incrustado.
 *
 * Trae lo que las tres comparten -- regresar, título y pantalla completa -- más las
 * acciones que solo usan algunas, cada una detrás de su bandera: compartir
 * (showShare, apagado en BelivPedia porque ya viene dentro de
 * app-repository-content-options) y abrir en una pestaña nueva (showOpenInNewTab,
 * solo el sitio incrustado). Lo que sea propio de una pantalla y no se repita
 * sigue yendo como contenido proyectado.
 *
 * A los botones que se proyecten hay que ponerles la clase `viewer-toolbar-action`
 * para que tomen el estilo de pastilla. Funciona porque el componente no encapsula
 * sus estilos (ViewEncapsulation.None), igual que el resto de los componentes de
 * @core.
 *
 * @example
 * <core-viewer-toolbar [title]="title" [isFullscreen]="isFullscreen"
 *                      (back)="goBack()" (fullscreenToggle)="toggleFullscreen()">
 *   <button type="button" class="viewer-toolbar-action" (click)="share()">...</button>
 * </core-viewer-toolbar>
 */
@Component({
  selector: 'core-viewer-toolbar',
  templateUrl: './core-viewer-toolbar.component.html',
  styleUrls: ['./core-viewer-toolbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class CoreViewerToolbarComponent {

  /** Título de la pantalla */
  @Input() title: string;

  /** Texto pequeño debajo del título. Si no viene, no se pinta */
  @Input() description: string;

  /** Key del texto del botón de regresar, por si una pantalla usa otra */
  @Input() backLabelKey: string = 'COMMON.BUTTONS.BACK';

  @Input() showBack: boolean = true;

  /** Se oculta cuando no hay nada que expandir (por ejemplo, un sitio que no cargó) */
  @Input() showFullscreen: boolean = true;

  /** Lo controla la pantalla, que es la dueña del elemento expandido */
  @Input() isFullscreen: boolean = false;

  /**
   * Muestra el botón de compartir. Va apagado por defecto: el visor de BelivPedia
   * ya trae su propio compartir dentro de app-repository-content-options y tendría
   * el botón dos veces.
   */
  @Input() showShare: boolean = false;

  /** Key del texto del botón de compartir, por si una pantalla usa otra */
  @Input() shareLabelKey: string = 'COMMON.ACTIONS.SHARE';

  /**
   * Muestra el enlace para abrir el contenido en una pestaña nueva. Lo usa el sitio
   * incrustado: muchos sitios no permiten ser incrustados y esa es la salida.
   */
  @Input() showOpenInNewTab: boolean = false;

  /** Url que abre el enlace de pestaña nueva */
  @Input() openInNewTabUrl: string;

  /** Key del texto del enlace de pestaña nueva */
  @Input() openInNewTabLabelKey: string = 'COMMON.EMBEDDED.OPEN_IN_NEW_TAB';

  /** Texto a mostrar si la key todavía no existe en el diccionario */
  @Input() openInNewTabFallback: string = 'Abrir en una pestaña nueva';

  @Output() back = new EventEmitter<void>();
  @Output() fullscreenToggle = new EventEmitter<void>();

  /** La pantalla decide qué significa compartir (copiar link, copiar url del archivo, ...) */
  @Output() share = new EventEmitter<void>();

  constructor(private _translateService: TranslateService) {}

  /**
   * Texto del enlace de pestaña nueva, con respaldo: si la key todavía no existe
   * en el diccionario se muestra el texto base en lugar del nombre de la key.
   */
  get openInNewTabLabel(): string {
    const value = this._translateService.instant(this.openInNewTabLabelKey);
    return !value || value === this.openInNewTabLabelKey ? this.openInNewTabFallback : value;
  }
}
